import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from '../../../../../src/hooks/useI18nNavigate';
import { useAuth } from '@edusense/shared-auth-core';
import { useQueryClient } from '@tanstack/react-query';
// Paraglide JS 메시지 import
import {
  actions_follow,
  actions_following,
  actions_unfollow,
  course_attachments_actions_upload,
  common_edit,
  common_login,
  userDashboard_followers,
  userDashboard_following
} from '@paraglide/messages';
import { css } from '@emotion/react';
import { Button } from "@edusense/shared-ui-kit";
import { Tooltip } from "@heroui/react";
// 테스트 식별자는 공유 상수에서만 로드 (Pure TestID)
import { PROFILE_PAGE_TEST_IDS } from '@edusense/shared-constants';
// REST API 클라이언트 사용 (Orval 자동 생성) - TODO: 실제 훅 연결 예정
import Upload from 'lucide-react/dist/esm/icons/upload';
import Edit from 'lucide-react/dist/esm/icons/edit';
import BookOpen from 'lucide-react/dist/esm/icons/book-open';
import MapPin from 'lucide-react/dist/esm/icons/map-pin';
import LinkIcon from 'lucide-react/dist/esm/icons/link';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';

import {
  FollowButton,
  LoginButton,
  ActionButtonsContainer,
  ProfileInfoContainer,
  ProfileMetaContainer,
  UserInfoBlock,
  UserDisplayName,
  UserUsername,
  BioText,
  MetaInfoWrapper,
  MetaItemsContainer,
  MetaItem,
  MetaLink
} from '../Profile.styles';

import { ScreenCompactMode } from '../base';

// GraphQL hooks from social-client - 팔로우 정보 조회 및 mutation
import { useFollowUserMutation, useUnfollowUserMutation, useGetUserByUsernameQuery, socialClient } from '@edusense/social-client';

interface ProfileHeaderProps {
  profile: any;
  isAuthenticated: boolean;
  refreshProfile?: () => Promise<void>;
  showToast?: (message: string, type: 'success' | 'error') => void;
  screenCompactMode: ScreenCompactMode;
  isMobileView: boolean;
  profileMetaRef: React.RefObject<HTMLDivElement>;
  usernameHint?: string;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  profile,
  isAuthenticated,
  refreshProfile,
  showToast,
  screenCompactMode,
  isMobileView,
  usernameHint,
  profileMetaRef
}) => {
  // 🚨 ProfileHeader 렌더링 확인
  console.log('🔥 [ProfileHeader] Component rendering!', {
    hasProfile: !!profile,
    profileData: profile?.data,
    usernameHint,
    isAuthenticated
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const followInFlightRef = useRef(false);
  const [isHovered, setIsHovered] = useState(false);
  // 🎯 social-server GraphQL에서 실제 팔로우 정보 조회
  // React Query 응답 구조: profile = useGetProfileByUsername의 data
  // API 응답 구조: { basicInfo: { username, ... }, profileInfo: {...}, ... }
  const targetUsername = profile?.basicInfo?.username || profile?.username || usernameHint;

  // 🔍 디버깅: targetUsername 값 확인 (강화된 로깅)
  console.log('🔍 [ProfileHeader] GraphQL Query Debug:', {
    profileBasicInfoUsername: profile?.basicInfo?.username,
    profileDirectUsername: profile?.username,
    usernameHint,
    targetUsername,
    finalEnabled: !!targetUsername,
    queryWillExecute: !!targetUsername && targetUsername.length > 0,
    profileObject: profile,
    profileKeys: profile ? Object.keys(profile) : 'no profile',
    hasProfile: !!profile,
    hasBasicInfo: !!profile?.basicInfo
  });
  
  // 🔍 Auth 사용자 정보에서 UUID 확인
  console.log('🔍 [ProfileHeader] Auth user 정보 분석:', {
    hasUser: !!user,
    userKeys: user ? Object.keys(user) : 'no user',
    userEntries: user ? Object.entries(user) : 'no user',
    userUuid: user?.uuid,
    userId: user?.id,
    userUserId: user?.userId,
    userUserIdUnderscore: user?.user_id
  });
  
  // 🚨 로깅으로만 확인 (alert 제거)
  if (!targetUsername) {
    console.error('🚨 [ProfileHeader] CRITICAL: targetUsername is missing!', {
      profile,
      usernameHint,
      profileDataUsername: profile?.data?.username
    });
  } else {
    console.log('✅ [ProfileHeader] targetUsername found:', targetUsername);
  }
  
  // GraphQL Query - social-server에서 팔로우 정보 조회
  // 🎯 로그인 없이도 기본 프로필 정보(팔로워/팔로잉 수 등) 조회 가능하도록 수정
  const enabledCondition = !!targetUsername; // isAuthenticated 제거
  
  console.log('🔍 [ProfileHeader] About to call useGetUserByUsernameQuery with:', {
    targetUsername,
    enabled: !!targetUsername,
    // GraphQLClient 상세 디버깅
    clientType: typeof socialClient,
    clientConstructor: (socialClient as any)?.constructor?.name || 'No constructor',
    clientKeys: Object.keys((socialClient as any) || {}),
    clientPrototype: Object.getPrototypeOf((socialClient as any) || {})?.constructor?.name || 'No prototype',
    // GraphQLClient 메서드 확인
    hasRequestMethod: typeof socialClient?.request === 'function',
    clientInstance: (socialClient as any) instanceof Object,
    clientString: String(socialClient),
    // GraphQLClient URL 및 설정 확인
    clientUrl: (socialClient as any)?.url || 'URL not accessible',
    clientHeaders: typeof (socialClient as any)?.requestConfig?.headers,
    // 쿼리 옵션 확인
    queryOptions: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      networkMode: 'always'
    }
  });
  
  // GraphQL Query - use generated hook for consistency
  const queryResult = useGetUserByUsernameQuery(
    socialClient,
    { username: String(targetUsername || '') },
    {
      enabled: !!targetUsername,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
  
  // Normalize GraphQL result shape: userByUsername returns a User object
  let socialUser: any = null;
  let isSocialLoading = false;
  let socialError: any = null;
  try {
    socialUser = queryResult.data?.userByUsername ?? null;
    isSocialLoading = queryResult.isLoading;
    socialError = queryResult.error;
    console.log('🔍 [ProfileHeader] GraphQL userByUsername fetched:', !!socialUser);
  } catch (error) {
    console.error('🚨 [ProfileHeader] GraphQL fetch failed:', error);
    socialUser = null;
    isSocialLoading = false;
    socialError = error;
  }
  
  // 🔍 React Query 상태 디버깅
  console.log('🔍 [ProfileHeader] React Query State:', {
    socialUser: socialUser ? 'present' : 'null',
    isSocialLoading,
    socialError: socialError ? socialError.message : 'none',
    targetUsername,
    queryEnabled: !!targetUsername,
    hasUserByUsername: !!socialUser,
    // 🔧 React Query 상태 더 자세히
    queryStatus: queryResult.status, // 'pending', 'error', 'success'
    fetchStatus: queryResult.fetchStatus, // 'fetching', 'paused', 'idle'
    isStale: queryResult.isStale, // 데이터가 stale인지 확인
    dataUpdatedAt: queryResult.dataUpdatedAt, // 마지막 업데이트 시간
    isFetching: queryResult.isFetching, // 현재 fetching 중인지
    isRefetching: queryResult.isRefetching // refetch 중인지
  });
  
  // 🔧 직접 GraphQL 쿼리 실행 테스트 (React Query 우회)
  // Removed direct request test; all queries go through codegen hooks with socialClient

  // 🎯 명확한 단일 경로 기반 상태 관리 - social-server GraphQL 데이터 활용
  // 비로그인 시에도 REST 프로필에서 팔로워/팔로잉 카운트는 표시 가능해야 함
  const restFollowersCount = profile?.data?.profileInfo?.followerCount ?? profile?.data?.profileInfo?.followersCount ?? 0;
  const restFollowingCount = profile?.data?.profileInfo?.followingCount ?? 0;
  const [localIsFollowing, setLocalIsFollowing] = useState<boolean | undefined>(undefined);
  const [localFollowerCount, setLocalFollowerCount] = useState<number | undefined>(restFollowersCount);
  
  // 🛡️ Optimistic update protection flag - prevents server data from overriding UI updates
  const [isOptimisticUpdate, setIsOptimisticUpdate] = useState(false);
  const isOptimisticUpdateRef = useRef(false);

  // 라우팅에 사용할 username 해상도 (UI 표기와 동일 경로 우선)
  const routeUsername = useMemo(() => {
    return (
      profile?.data?.username ||
      targetUsername ||
      socialUser?.username ||
      usernameHint ||
      ''
    );
  }, [profile?.data?.username, targetUsername, socialUser?.username, usernameHint]);

  // social-server에서 가져온 실제 팔로우 정보로 상태 업데이트 (방어적 코딩)
  useEffect(() => {
    console.log('[ProfileHeader] 🔍 useEffect triggered:', {
      hasSocialUser: !!socialUser,
      isOptimisticUpdate,
      isOptimisticUpdateRef: isOptimisticUpdateRef.current,
      shouldBlock: isOptimisticUpdate || isOptimisticUpdateRef.current
    });
    
    // 🛡️ ENHANCED protection: Double check both state and ref to prevent race conditions
    if (socialUser && !isOptimisticUpdate && !isOptimisticUpdateRef.current) {
      // 안전한 관계 정보 추출
      const relationship = socialUser.relationshipWithCurrentUser;
      const isFollowing = relationship?.isFollowing ?? false;
      
      // 안전한 통계 정보 추출 (다중 fallback)
      const stats = socialUser.stats;
      const followersCount = stats?.followersCount ?? 0;
      
      console.log('[ProfileHeader] 🔄 Updating from server data:', {
        isFollowing,
        followersCount,
        currentLocalCount: localFollowerCount
      });
      
      // 🎯 Only update from server data if NOT in optimistic update state
      setLocalIsFollowing(isFollowing);
      setLocalFollowerCount(followersCount);
      
      console.log('[ProfileHeader] ✅ State updated from server data:', {
        hasStats: !!stats,
        followersCount,
        isFollowing,
        rawData: socialUser,
        isOptimisticUpdate: false,
        isOptimisticUpdateRef: isOptimisticUpdateRef.current
      });
    } else if (isOptimisticUpdate || isOptimisticUpdateRef.current) {
      // 낙관적 상태 유지: 서버 값으로 덮어쓰지 않되, 서버와 동기화되면 보호 해제
      const serverFollowing = socialUser?.relationshipWithCurrentUser?.isFollowing;
      const serverFollowersCount = socialUser?.stats?.followersCount;
      const followingAligned = typeof serverFollowing === 'boolean' && serverFollowing === localIsFollowing;
      const countAligned = typeof serverFollowersCount === 'number' && typeof localFollowerCount === 'number'
        ? serverFollowersCount === localFollowerCount
        : false;
      console.log('[ProfileHeader] 🛡️ Skipping server update (optimistic ON)', {
        isOptimisticUpdate,
        isOptimisticUpdateRef: isOptimisticUpdateRef.current,
        serverFollowing,
        localIsFollowing,
        serverFollowersCount,
        localFollowerCount,
        followingAligned,
        countAligned
      });
      // 서버 상태가 로컬 낙관적 상태와 일치하면 보호 플래그 해제
      if (followingAligned || countAligned) {
        setIsOptimisticUpdate(false);
        isOptimisticUpdateRef.current = false;
        console.log('[ProfileHeader] ✅ Optimistic protection lifted: server aligned with local state');
      }
    } else if (!socialUser && !isOptimisticUpdate && !isOptimisticUpdateRef.current) {
      // 로그인하지 않았거나 GraphQL 사용 불가 시 REST 프로필 카운트를 표시
      setLocalFollowerCount(restFollowersCount);
    }
  }, [socialUser, isOptimisticUpdate, restFollowersCount]);
  
  // GraphQL mutation hooks from social-client - using React Query v5 compatible hooks
  const followUserMutation = useFollowUserMutation(socialClient, {
    onSuccess: (data) => {
      console.log('✅ Follow mutation successful:', data);
    },
    onError: (error) => {
      console.error('❌ Follow mutation failed:', error);
    }
  });

  const unfollowUserMutation = useUnfollowUserMutation(socialClient, {
    onSuccess: (data) => {
      console.log('✅ Unfollow mutation successful:', data);
    },
    onError: (error) => {
      console.error('❌ Unfollow mutation failed:', error);
    }
  });
  
  // 🔧 Debugging: mutation 객체 구조 확인
  console.log('🔍 [ProfileHeader] Mutation objects debug:', {
    followMutationType: typeof followUserMutation,
    followMutationKeys: Object.keys(followUserMutation || {}),
    hasMutateAsync: typeof followUserMutation?.mutateAsync === 'function',
    hasMutate: typeof followUserMutation?.mutate === 'function',
    unfollowMutationType: typeof unfollowUserMutation,
    unfollowMutationKeys: Object.keys(unfollowUserMutation || {}),
    hasUnfollowMutateAsync: typeof unfollowUserMutation?.mutateAsync === 'function',
    hasUnfollowMutate: typeof unfollowUserMutation?.mutate === 'function'
  });

  // 🎯 명확한 단일 경로: GraphQL 구조 분석 완료! 
  // ✅ 올바른 데이터 구조: socialUser.userId (GetUserByUsernameQuery)
  // profile.data는 user-server REST API, socialUser는 social GraphQL
  const socialTargetUserId = socialUser?.userId ? String(socialUser.userId) : undefined;
  
  // 🛡️ Fallback: REST API profile 데이터에서 userId 추출 (GraphQL 실패시 대안)
  // 🔍 REST API profile 데이터 구조 완전 분석
  console.log('🔍 [ProfileHeader] REST API profile 전체 구조 분석:', {
    profile: profile,
    profileData: profile?.data,
    profileDataKeys: profile?.data ? Object.keys(profile.data) : 'no data',
    profileDataEntries: profile?.data ? Object.entries(profile.data) : 'no data',
    // 일반적인 사용자 ID 필드들 체크
    possibleIdFields: {
      id: profile?.data?.id,
      uuid: profile?.data?.uuid,
      userId: profile?.data?.userId,
      user_id: profile?.data?.user_id,
      userUuid: profile?.data?.userUuid,
      user_uuid: profile?.data?.user_uuid,
      uid: profile?.data?.uid,
      pk: profile?.data?.pk,
      primaryKey: profile?.data?.primaryKey,
      identifier: profile?.data?.identifier
    }
  });

  // numeric id는 사용 금지(deprecated). UUID만 허용.
  const fallbackTargetUserId = profile?.data?.uuid;
  
  // 🔧 TEMPORARY FIX: GraphQL이 작동하지 않을 때 REST API 데이터 사용
  // 최종 대상 ID: GraphQL UUID 우선, 없으면 UUID가 있을 때만 사용. 숫자 ID 사용 금지.
  const finalTargetUserId = socialTargetUserId || fallbackTargetUserId;
  
  console.log('🔧 [ProfileHeader] UserId resolution debug:', {
    socialTargetUserId,
    fallbackTargetUserId,
    finalTargetUserId,
    profileId: profile?.data?.id,
    profileUuid: profile?.data?.uuid,
    profileUserId: profile?.data?.userId,
    profileUserIdUnderscore: profile?.data?.user_id,
    willUseFallback: !socialTargetUserId && !!fallbackTargetUserId
  });

  const handleFollowToggle = async () => {
    if (followInFlightRef.current || isFollowLoading) {
      console.log('[ProfileHeader] Follow toggle ignored (in-flight)');
      return;
    }
    followInFlightRef.current = true;
    console.log('🎯 handleFollowToggle STARTED!');
    console.error('🎯 handleFollowToggle ERROR LEVEL LOG!');
    
    // 🔍 더 상세한 디버깅 정보
    const debugInfo = {
      hasProfile: !!profile,
      profileData: profile ? 'present' : 'null',
      isAuthenticated,
      hasSocialData: !!socialUser,
      socialDataUserByUsername: socialUser ? 'present' : 'null',
      socialTargetUserId,
      socialTargetUserIdType: typeof socialTargetUserId,
      fallbackTargetUserId,
      finalTargetUserId,
      finalTargetUserIdType: typeof finalTargetUserId,
      isSocialLoading,
      socialUserKeys: socialUser ? Object.keys(socialUser) : 'null',
      socialUserId: socialUser?.userId,
      socialUsername: socialUser?.username
    };
    
    console.error('🔍 DETAILED DEBUG INFO:', debugInfo);
      console.error('🔍 SOCIAL TARGET USER ID DETAILS:', {
        socialTargetUserId,
        socialTargetUserIdValue: socialUser?.userId,
        socialTargetUserIdStringified: socialUser?.userId ? String(socialUser.userId) : 'UNDEFINED'
      });
    
    if (!profile || !isAuthenticated) {
      const blockReason = {
        hasProfile: !!profile, 
        isAuthenticated, 
        hasSocialData: !!socialUser,
        socialTargetUserId,
        isSocialLoading,
        profileKeys: profile ? Object.keys(profile) : 'null',
        socialUserKeys: socialUser ? Object.keys(socialUser) : 'null'
      };
      
      console.warn('[ProfileHeader] Follow action blocked:', blockReason);
      console.error('[ProfileHeader] ❌ FUNCTION BLOCKED!', blockReason);
      console.error('🚨 EXACT FAILURE CONDITION:', {
        profileFail: !profile,
        authFail: !isAuthenticated,
        socialDataFail: !socialUser,
        userIdFail: !socialTargetUserId
      });
      if (!isAuthenticated) {
        if (showToast) showToast('로그인이 필요합니다', 'error');
        try { navigate('/landing'); } catch {}
      }
      return;
    }
    
    console.log('[ProfileHeader] Starting follow toggle:', { 
      currentFollowing: localIsFollowing, 
      targetUser: socialUser?.username || profile?.data?.username,
      socialTargetUserId,
      finalTargetUserId
    });
    
    setIsFollowLoading(true);
    const previousFollowing = localIsFollowing;
    const previousCount = localFollowerCount ?? 0;

    try {
      // Ensure target UUID is available; resolve by username if needed
      let ensuredTargetId: string | undefined = finalTargetUserId ? String(finalTargetUserId) : undefined;
      if (!ensuredTargetId && targetUsername) {
        try {
          const query = `
            query GetUserId($username: String!) {
              userByUsername(username: $username) { userId username }
            }
          `;
          const resp = await socialClient.request(query, { username: targetUsername });
          ensuredTargetId = resp?.userByUsername?.userId ? String(resp.userByUsername.userId) : undefined;
          console.log('[ProfileHeader] Resolved target UUID via GraphQL:', { targetUsername, ensuredTargetId });
        } catch (e) {
          console.warn('[ProfileHeader] Failed to resolve target UUID via GraphQL', e);
        }
      }
      if (!ensuredTargetId) {
        console.error('[ProfileHeader] ❌ No target UUID could be resolved. Aborting.');
        if (showToast) showToast('대상 사용자를 확인할 수 없습니다', 'error');
        return;
      }
      if (localIsFollowing) {
        // 🛡️ Activate optimistic update protection
        setIsOptimisticUpdate(true);
        isOptimisticUpdateRef.current = true;
        
        // Optimistic update: decrease follower count
        setLocalIsFollowing(false);
        setLocalFollowerCount((prev) => Math.max(0, (prev ?? 0) - 1));
        
        console.log('[ProfileHeader] Attempting unfollow mutation...');
        
        // 🔧 Safe mutation call with fallback
        let result;
        const targetId = String(finalTargetUserId || '');
        if (typeof unfollowUserMutation?.mutateAsync === 'function') {
          result = await unfollowUserMutation.mutateAsync({ userId: ensuredTargetId });
        } else {
          // Fallback: Promise-based mutation call
          result = await new Promise((resolve, reject) => {
            unfollowUserMutation.mutate(
              { userId: ensuredTargetId },
              {
                onSuccess: (data) => resolve(data),
                onError: (error) => reject(error)
              }
            );
          });
        }
        
        console.log('[ProfileHeader] Unfollow mutation result:', result);
        
        if (result?.unfollowUser?.success) {
          try {
            const userKey = useGetUserByUsernameQuery.getKey({ username: String(targetUsername) });
            queryClient.setQueryData(userKey, (old: any) => {
              if (!old?.userByUsername) return old;
              const prevCount = old.userByUsername?.stats?.followersCount ?? restFollowersCount ?? 0;
              return {
                ...old,
                userByUsername: {
                  ...old.userByUsername,
                  stats: {
                    ...(old.userByUsername.stats || {}),
                    followersCount: Math.max(0, prevCount - 1),
                    followingCount: old.userByUsername?.stats?.followingCount ?? 0,
                    postsCount: old.userByUsername?.stats?.postsCount ?? 0,
                  },
                  relationshipWithCurrentUser: {
                    ...(old.userByUsername.relationshipWithCurrentUser || {}),
                    isFollowing: false,
                  }
                }
              };
            });
          } catch (e) { console.error('Optimistic cache update (unfollow) failed:', e); }
          // GraphQL 캐시 무효화하여 최신 데이터 로드
          try {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: useGetUserByUsernameQuery.getKey({ username: String(targetUsername) }) }),
              queryClient.invalidateQueries({
                predicate: (q) => {
                  if (!Array.isArray(q.queryKey)) return false;
                  const k = q.queryKey[0];
                  return (
                    k === 'GetFollowers' ||
                    k === 'GetFollowing' ||
                    k === 'GetFollowersByUsername' ||
                    k === 'GetFollowingByUsername'
                  );
                },
              }),
            ]);
          } catch (err) { console.error('Cache invalidation error:', err); }
          
          // 서버 동기화가 끝날 때까지 낙관적 보호 유지 (useEffect에서 정렬 확인 후 해제)
          
          // 성공 시 프로필 업데이트
          if (refreshProfile) await refreshProfile();
          const targetUsername = socialUser?.username || profile?.data?.username || 'User';
          if (showToast) showToast(`${targetUsername} 언팔로우 성공`, 'success');
          console.log('[ProfileHeader] ✅ Unfollow successful');
        } else {
          // 🛡️ Reset optimistic update flag on failure
          setIsOptimisticUpdate(false);
          isOptimisticUpdateRef.current = false;
          
          // Revert optimistic update
          setLocalIsFollowing(previousFollowing);
          setLocalFollowerCount(previousCount);
          const errorMsg = result?.unfollowUser?.message || '언팔로우 실패';
          console.error('[ProfileHeader] ❌ Unfollow failed:', errorMsg);
          throw new Error(errorMsg);
        }
      } else {
        // 🛡️ Activate optimistic update protection
        setIsOptimisticUpdate(true);
        isOptimisticUpdateRef.current = true;
        
        // Optimistic update: increase follower count
        setLocalIsFollowing(true);
        console.error('🔄 BEFORE follower count update - current count:', localFollowerCount);
        setLocalFollowerCount((prev) => {
          const newCount = (prev ?? 0) + 1;
          console.error('🔄 Local state updated: following=true, count=' + newCount);
          console.error('🎯 FOLLOWER COUNT INCREASED FROM', prev, 'TO', newCount);
          return newCount;
        });
        
        // 🔥 추가 로그: state 업데이트 직후 확인
        setTimeout(() => {
          console.error('🔥 ASYNC CHECK: localFollowerCount after state update:', localFollowerCount);
        }, 0);
        
        console.log('[ProfileHeader] Attempting follow mutation...');
        console.error('🚀 FOLLOW MUTATION PARAMS:', { 
          userId: String(finalTargetUserId), 
          rawUserId: finalTargetUserId,
          socialTargetUserId,
          fallbackTargetUserId,
          mutationAvailable: !!followUserMutation,
          mutateAsyncAvailable: typeof followUserMutation?.mutateAsync === 'function',
          mutateAvailable: typeof followUserMutation?.mutate === 'function'
        });
        
        // 🔧 Safe mutation call with fallback
        let result;
        const targetId = String(finalTargetUserId || '');
        if (typeof followUserMutation?.mutateAsync === 'function') {
          result = await followUserMutation.mutateAsync({ userId: ensuredTargetId });
        } else {
          // Fallback: Promise-based mutation call
          result = await new Promise((resolve, reject) => {
            followUserMutation.mutate(
              { userId: ensuredTargetId },
              {
                onSuccess: (data) => {
                  console.log('🎯 Follow mutation success via Promise:', data);
                  resolve(data);
                },
                onError: (error) => {
                  console.error('❌ Follow mutation error via Promise:', error);
                  reject(error);
                }
              }
            );
          });
        }
        
        console.log('[ProfileHeader] Follow mutation result:', result);
        console.error('🎯 FOLLOW MUTATION COMPLETE:', { result, success: result?.followUser?.success });
        
        if (result?.followUser?.success) {
          try {
            const userKey = useGetUserByUsernameQuery.getKey({ username: String(targetUsername) });
            queryClient.setQueryData(userKey, (old: any) => {
              if (!old?.userByUsername) return old;
              const prevCount = old.userByUsername?.stats?.followersCount ?? restFollowersCount ?? 0;
              return {
                ...old,
                userByUsername: {
                  ...old.userByUsername,
                  stats: {
                    ...(old.userByUsername.stats || {}),
                    followersCount: prevCount + 1,
                    followingCount: old.userByUsername?.stats?.followingCount ?? 0,
                    postsCount: old.userByUsername?.stats?.postsCount ?? 0,
                  },
                  relationshipWithCurrentUser: {
                    ...(old.userByUsername.relationshipWithCurrentUser || {}),
                    isFollowing: true,
                  }
                }
              };
            });
          } catch (e) { console.error('Optimistic cache update (follow) failed:', e); }
          console.error('🎉 FOLLOW SUCCESS - Starting UI updates...');
          
          // 🎯 Keep the optimistic update (don't revert it)
          console.error('🔄 Maintaining optimistic state: following=true, count=' + localFollowerCount);
          
          // GraphQL 캐시 무효화하여 서버에서 최신 데이터 가져오기 (백그라운드에서)
          console.error('🗂️ Invalidating GraphQL caches...');
          try {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: useGetUserByUsernameQuery.getKey({ username: String(targetUsername) }) }),
              queryClient.invalidateQueries({
                predicate: (q) => {
                  if (!Array.isArray(q.queryKey)) return false;
                  const k = q.queryKey[0];
                  return (
                    k === 'GetFollowers' ||
                    k === 'GetFollowing' ||
                    k === 'GetFollowersByUsername' ||
                    k === 'GetFollowingByUsername'
                  );
                },
              }),
            ]);
          } catch (err) { console.error('Cache invalidation error:', err); }
          // 서버 동기화가 끝날 때까지 낙관적 보호 유지 (useEffect에서 정렬 확인 후 해제)
          
          // 성공 알림
          const targetUsername = socialUser?.username || profile?.data?.username || 'User';
          if (showToast) showToast(`${targetUsername} 팔로우 성공`, 'success');
          console.log('[ProfileHeader] ✅ Follow successful - Optimistic update maintained');
        } else {
          // 🛡️ Reset optimistic update flag on failure
          setIsOptimisticUpdate(false);
          isOptimisticUpdateRef.current = false;
          
          // Revert optimistic update
          setLocalIsFollowing(previousFollowing);
          setLocalFollowerCount(previousCount);
          const errorMsg = result?.followUser?.message || '팔로우 실패';
          console.error('[ProfileHeader] ❌ Follow failed:', errorMsg);
          throw new Error(errorMsg);
        }
      }
    } catch (err) {
      console.error('[ProfileHeader] Follow/Unfollow exception:', err);
      
      // 🛡️ Reset optimistic update flag on exception
      setIsOptimisticUpdate(false);
      
      // Revert to previous state
      setLocalIsFollowing(previousFollowing);
      setLocalFollowerCount(previousCount);
      
      // Try to get fresh server state if available
      if (socialUser?.relationshipWithCurrentUser?.isFollowing !== undefined) {
        setLocalIsFollowing(socialUser.relationshipWithCurrentUser.isFollowing);
      }
      if (socialUser?.stats?.followersCount !== undefined) {
        setLocalFollowerCount(socialUser.stats.followersCount);
      }
      
      const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류';
      if (showToast) showToast(`팔로우 오류: ${errorMessage}`, 'error');
    } finally {
      setIsFollowLoading(false);
      followInFlightRef.current = false;
      console.log('[ProfileHeader] Follow toggle completed, final state:', { 
        isFollowing: localIsFollowing, 
        count: localFollowerCount 
      });
    }
  };

  // 🎯 명확한 단일 경로: 다중 검증으로 현재 사용자 확인
  const currentUserId = user?.id || user?.uuid;
  const profileUserId = profile?.data?.id || profile?.data?.uuid;
  const profileUsername = profile?.data?.username;
  const currentUsername = user?.username;

  // 🛡️ 명확한 단일 경로: 서버 플래그에 의존하지 않고 ID/username 기반으로만 판별
  const isSameById = Boolean(currentUserId && profileUserId && String(currentUserId) === String(profileUserId));
  const isSameByUsername = Boolean(
    currentUsername && targetUsername && currentUsername.toLowerCase() === String(targetUsername).toLowerCase()
  );
  const isCurrentUserComputed = isSameById || isSameByUsername;
  
  // 🔍 디버깅: 현재 사용자 판별 로직 확인
  console.log('🔍 [ProfileHeader] Current User Detection:', {
    currentUserId,
    profileUserId,
    currentUsername, 
    profileUsername,
    authIsCurrentUser: profile?.data?.auth?.isCurrentUser,
    finalIsCurrentUser: isCurrentUserComputed,
    userObject: user,
    profileData: profile?.data
  });
  // 백엔드 플래그와 불일치 시 경고 (가시화)
  if (profile?.data?.auth?.isCurrentUser === true && !isCurrentUserComputed) {
    console.warn('[ProfileHeader] Backend auth.isCurrentUser=true but computed self=false. Falling back to computed=false to show follow button.', {
      currentUserId, profileUserId, currentUsername, targetUsername
    });
  }

  const renderProfileButtons = () => {
    // 🛡️ 자신의 프로필인 경우 팔로우 버튼 숨김
    if (isCurrentUserComputed) {
      console.log('🏠 [ProfileHeader] 현재 사용자 자신의 프로필 - 프로젝트 생성 버튼들 표시');
      return (
        <ActionButtonsContainer>
          <Tooltip content={`프로젝트 ${course_attachments_actions_upload()}`} placement="bottom">
            <Button
              variant="primary"
              size="md"
              shape="circular"
              onClick={() => navigate('/project/new')}
              icon={<Upload size={isMobileView ? 16 : 18} />}
              iconOnly={true}
              aria-label={`프로젝트 ${course_attachments_actions_upload()}`}
              title={`프로젝트 ${course_attachments_actions_upload()}`}
              style={{
                minWidth: isMobileView ? '36px' : '44px',
                width: isMobileView ? '36px' : '44px',
                height: isMobileView ? '36px' : '44px',
                padding: '0',
                flexShrink: 0
              }}
            />
          </Tooltip>
          <Tooltip content={`강의 ${course_attachments_actions_upload()}`} placement="bottom">
            <Button
              variant="primary"
              size="md"
              shape="circular"
              onClick={() => navigate(`/learn/course/new/edit`)}
              icon={<BookOpen size={isMobileView ? 16 : 18} />}
              iconOnly={true}
              aria-label={`강의 ${course_attachments_actions_upload()}`}
              title={`강의 ${course_attachments_actions_upload()}`}
              style={{
                minWidth: isMobileView ? '36px' : '44px',
                width: isMobileView ? '36px' : '44px',
                height: isMobileView ? '36px' : '44px',
                padding: '0',
                flexShrink: 0
              }}
            />
          </Tooltip>
          <Tooltip content={`로드맵 ${course_attachments_actions_upload()}`} placement="bottom">
            <Button
              variant="primary"
              size="md"
              shape="circular"
              onClick={() => navigate('/learn/roadmap/new')}
              icon={<MapPin size={isMobileView ? 16 : 18} />}
              iconOnly={true}
              aria-label={`로드맵 ${course_attachments_actions_upload()}`}
              title={`로드맵 ${course_attachments_actions_upload()}`}
              style={{
                minWidth: isMobileView ? '36px' : '44px',
                width: isMobileView ? '36px' : '44px',
                height: isMobileView ? '36px' : '44px',
                padding: '0',
                flexShrink: 0
              }}
            />
          </Tooltip>
          <Button
            data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_EDIT_BUTTON}
            variant="outline"
            size="md"
            shape="circular"
            onClick={() => navigate('/user/edit')}
            icon={<Edit size={isMobileView ? 14 : 18} />}
            iconOnly={true}
            aria-label={`프로필 ${common_edit()}`}
            title={`프로필 ${common_edit()}`}
            className="profile-edit-btn"
            style={{
              minWidth: isMobileView ? '32px' : '44px',
              width: isMobileView ? '32px' : '44px',
              height: isMobileView ? '32px' : '44px',
              padding: '0',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobileView ? '14px' : '16px'
            }}
          />
        </ActionButtonsContainer>
      );
    }
    
    // 🔐 로그인하지 않은 경우 - 로그인 버튼 표시
    if (!isAuthenticated) {
      console.log('🔐 [ProfileHeader] 미로그인 상태 - 로그인 버튼 표시');
      return (
        <LoginButton onClick={() => navigate('/home?redirect=' + location.pathname)}>
          {common_login()}하여 팔로우
        </LoginButton>
      );
    }
    
    // 👥 다른 사용자의 프로필인 경우 - 팔로우 버튼 표시
    console.log('👥 [ProfileHeader] 다른 사용자 프로필 - 팔로우/메시지 버튼 표시');
    return (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <FollowButton
          data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_FOLLOW_BUTTON}
          $isFollowing={Boolean(localIsFollowing)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // 🔥 디버깅 로그 (alert 제거)
            console.log('🔥🔥🔥 BUTTON CLICK DETECTED! 🔥🔥🔥');
            console.error('🔥🔥🔥 ERROR LEVEL LOG - BUTTON CLICKED! 🔥🔥🔥');
            
            console.log('[ProfileHeader] 🔵 Follow button clicked!', {
              hasProfile: !!profile,
              isAuthenticated,
              socialTargetUserId,
              localIsFollowing,
              isFollowLoading,
              profileData: profile?.data,
              event: e
            });
            
            // 조건별 상세 로깅
            if (!profile) {
              console.error('[ProfileHeader] ❌ BLOCKED: No profile data');
            }
            if (!isAuthenticated) {
              console.error('[ProfileHeader] ❌ BLOCKED: Not authenticated');
            }
            if (!socialTargetUserId) {
              console.error('[ProfileHeader] ❌ BLOCKED: No socialTargetUserId');
            }
            
            handleFollowToggle();
          }}
          // Allow click even if social query is loading; we resolve UUID on demand
          disabled={isFollowLoading}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {(() => {
            // 🔧 버튼 텍스트 디버깅
            console.log('🔤 [ProfileHeader] Button text logic:', {
              isFollowLoading,
              localIsFollowing,
              isHovered,
            });

            const following = Boolean(localIsFollowing);
            const text = following ? (isHovered ? actions_unfollow() : actions_following()) : actions_follow();
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {text}
                {isFollowLoading && (
                  <Loader2 css={css`height: 0.9rem; width: 0.9rem; animation: spin 1s linear infinite;`} />
                )}
              </span>
            );
          })()}
        </FollowButton>
        {/* 메시지 버튼 (DM 시작) - 테스트 식별자 추가 */}
        <button
          data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_MESSAGE_BUTTON}
          className="px-3 py-2 rounded-md border text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          onClick={() => {
            try {
              // Open People Chat in right sidebar via global event
              window.dispatchEvent(new Event('open-people-chat'));
            } catch {}
          }}
        >메시지</button>
      </div>
    );
  };

  return (
    <ProfileMetaContainer 
      ref={profileMetaRef}
      className={`profile-meta-container ${isMobileView ? 'mobile-view' : ''}`}
      data-compact-mode={screenCompactMode}
      data-mobile-view={isMobileView}
    >
      <ProfileInfoContainer className="profile-info-container">
        <UserInfoBlock className="user-info-block">
          <UserDisplayName className="user-display-name" data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_DISPLAY_NAME}>
            {socialUser?.displayName || profile?.data?.basicInfo?.displayName || profile?.data?.username || usernameHint || 'Unknown User'}
          </UserDisplayName>
          <UserUsername className="user-username" data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_USERNAME}>
            @{socialUser?.username || profile?.data?.username || usernameHint || 'unknown'}
          </UserUsername>
        </UserInfoBlock>
        {renderProfileButtons()}
      </ProfileInfoContainer>

      {/* Followers/Following stats with i18n labels */}
      <div
        className="profile-stats"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          fontSize: '0.875rem',
          color: 'var(--theme-text-secondary, #6b7280)',
          marginTop: '0.5rem'
        }}
      >
        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (routeUsername) navigate(`/profile/${routeUsername}/followers`);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (routeUsername) navigate(`/profile/${routeUsername}/followers`);
            }
          }}
          title={userDashboard_followers()}
        >
          <span
            data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_FOLLOWERS_COUNT}
            style={{ fontWeight: 'bold', fontSize: '1rem', color: 'var(--theme-text, #111827)' }}
          >
            {(() => {
              const displayCount = localFollowerCount ?? 0;
              return displayCount;
            })()}
          </span>
          <span data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_FOLLOWERS_LABEL}>
            {userDashboard_followers()}
          </span>
        </div>
        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (routeUsername) navigate(`/profile/${routeUsername}/following`);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (routeUsername) navigate(`/profile/${routeUsername}/following`);
            }
          }}
          title={userDashboard_following()}
        >
          <span
            data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_FOLLOWING_COUNT}
            style={{ fontWeight: 'bold', fontSize: '1rem', color: 'var(--theme-text, #111827)' }}
          >
            {socialUser?.stats?.followingCount ?? restFollowingCount}
          </span>
          <span data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_FOLLOWING_LABEL}>
            {userDashboard_following()}
          </span>
        </div>
      </div>

      {(profile?.data?.bio || socialUser?.bio) && (
        <MetaInfoWrapper className="meta-info-wrapper">
          <BioText className="bio-text" data-testid={PROFILE_PAGE_TEST_IDS.PROFILE_BIO}>
            {profile?.data?.bio ?? socialUser?.bio}
          </BioText>
          <MetaItemsContainer className="meta-items-container">
            {profile.data?.location && (
              <MetaItem>
                <MapPin css={css`height: 1rem; width: 1rem;`} />
                <span>{profile.data.location}</span>
              </MetaItem>
            )}
            {profile.data?.website && (
              <MetaItem>
                <LinkIcon css={css`height: 0.85rem; width: 0.85rem; margin-right: 3px;`} />
                <MetaLink
                  href={profile.data.website.startsWith('http') ? profile.data.website : `http://${profile.data.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {profile.data.website}
                </MetaLink>
              </MetaItem>
            )}
          </MetaItemsContainer>
        </MetaInfoWrapper>
      )}
    </ProfileMetaContainer>
  );
};
